import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Bell,
  BellOff,
  User,
  FileImage,
  Book,
  Palette,
  MessageSquare,
  AlertTriangle,
  Check,
  CheckCheck,
  Trash2,
  RefreshCw,
  Filter,
  ChevronDown,
  Clock,
  ExternalLink,
  Loader2,
  X
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { useAuth } from '../../App';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

const API = '/api';

// Notification type icons and colors
const NOTIFICATION_CONFIG = {
  user_signup: {
    icon: User,
    color: 'text-green-400',
    bgColor: 'bg-green-500/10',
    borderColor: 'border-green-500/30',
  },
  comic_created: {
    icon: FileImage,
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/10',
    borderColor: 'border-blue-500/30',
  },
  chapter_published: {
    icon: Book,
    color: 'text-purple-400',
    bgColor: 'bg-purple-500/10',
    borderColor: 'border-purple-500/30',
  },
  artwork_uploaded: {
    icon: Palette,
    color: 'text-pink-400',
    bgColor: 'bg-pink-500/10',
    borderColor: 'border-pink-500/30',
  },
  comment_created: {
    icon: MessageSquare,
    color: 'text-cyan-400',
    bgColor: 'bg-cyan-500/10',
    borderColor: 'border-cyan-500/30',
  },
  content_reported: {
    icon: AlertTriangle,
    color: 'text-red-400',
    bgColor: 'bg-red-500/10',
    borderColor: 'border-red-500/30',
  },
  novel_created: {
    icon: Book,
    color: 'text-indigo-400',
    bgColor: 'bg-indigo-500/10',
    borderColor: 'border-indigo-500/30',
  },
  novel_chapter_published: {
    icon: Book,
    color: 'text-violet-400',
    bgColor: 'bg-violet-500/10',
    borderColor: 'border-violet-500/30',
  },
  creator_verified: {
    icon: Check,
    color: 'text-emerald-400',
    bgColor: 'bg-emerald-500/10',
    borderColor: 'border-emerald-500/30',
  },
};

const getNotificationConfig = (type) => {
  return NOTIFICATION_CONFIG[type] || {
    icon: Bell,
    color: 'text-gray-400',
    bgColor: 'bg-gray-500/10',
    borderColor: 'border-gray-500/30',
  };
};

// Format relative time
const formatTimeAgo = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);
  
  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return date.toLocaleDateString();
};

const AdminNotifications = () => {
  useDocumentTitle('Notifications - Admin');
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // State
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [counts, setCounts] = useState({ unread: 0, total: 0 });
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all_types');
  
  // Selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  
  // Actions
  const [actionLoading, setActionLoading] = useState(false);

  // Check admin access
  useEffect(() => {
    if (user && !user.is_admin && user.role !== 'admin') {
      navigate('/');
      toast.error('Admin access required');
    }
  }, [user, navigate]);

  // Fetch notification count
  const fetchCount = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/notifications/count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCounts(response.data);
    } catch (error) {
      console.error('Error fetching count:', error);
    }
  }, []);

  // Fetch notifications
  const fetchNotifications = useCallback(async (reset = false) => {
    if (!user) return;
    
    try {
      if (reset) {
        setLoading(true);
        setNotifications([]);
        setNextCursor(null);
      }
      
      const params = new URLSearchParams({ limit: '50' });
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (typeFilter !== 'all_types') params.append('type', typeFilter);
      if (!reset && nextCursor) params.append('cursor', nextCursor);
      
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/notifications?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (reset) {
        setNotifications(response.data.notifications || []);
      } else {
        setNotifications(prev => [...prev, ...(response.data.notifications || [])]);
      }
      
      setHasMore(response.data.has_more || false);
      setNextCursor(response.data.next_cursor || null);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, [user, statusFilter, typeFilter, nextCursor]);

  // Initial load
  useEffect(() => {
    fetchNotifications(true);
    fetchCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, typeFilter]);

  // Handle selection
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(notifications.map(n => n.id)));
    }
    setSelectAll(!selectAll);
  };

  // Mark as read
  const handleMarkRead = async (ids = null) => {
    const idsToMark = ids || Array.from(selectedIds);
    if (idsToMark.length === 0) return;
    
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/admin/notifications/mark-read`,
        { notification_ids: idsToMark },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Marked ${idsToMark.length} as read`);
      setSelectedIds(new Set());
      setSelectAll(false);
      fetchNotifications(true);
      fetchCount();
    } catch (error) {
      toast.error('Failed to mark as read');
    } finally {
      setActionLoading(false);
    }
  };

  // Mark all as read
  const handleMarkAllRead = async () => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/admin/notifications/mark-all-read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('All notifications marked as read');
      fetchNotifications(true);
      fetchCount();
    } catch (error) {
      toast.error('Failed to mark all as read');
    } finally {
      setActionLoading(false);
    }
  };

  // Delete notification
  const handleDelete = async (id) => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/notifications/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Notification deleted');
      setNotifications(prev => prev.filter(n => n.id !== id));
      fetchCount();
    } catch (error) {
      toast.error('Failed to delete');
    } finally {
      setActionLoading(false);
    }
  };

  // Get target link
  const getTargetLink = (notification) => {
    const { target_type, target_id } = notification;
    if (!target_id) return null;
    
    switch (target_type) {
      case 'series':
        return `/series/${target_id}`;
      case 'novel':
        return `/novels/${target_id}`;
      case 'artwork':
        return `/art/${target_id}`;
      case 'user':
        return `/admin/users/${target_id}`;
      case 'report':
        return `/admin/reports/${target_id}`;
      default:
        return null;
    }
  };

  // Render notification item
  const renderNotification = (notification) => {
    const config = getNotificationConfig(notification.type);
    const IconComponent = config.icon;
    const isUnread = !notification.read_at;
    const isSelected = selectedIds.has(notification.id);
    const targetLink = getTargetLink(notification);
    
    return (
      <div
        key={notification.id}
        className={`flex items-start gap-4 p-4 rounded-lg border transition-all ${
          isUnread 
            ? `${config.bgColor} ${config.borderColor}` 
            : 'bg-zinc-900/50 border-zinc-800'
        } ${isSelected ? 'ring-2 ring-orange-500/50' : ''}`}
        data-testid={`notification-${notification.id}`}
      >
        {/* Checkbox */}
        <Checkbox
          checked={isSelected}
          onCheckedChange={() => toggleSelect(notification.id)}
          className="mt-1 data-[state=checked]:bg-orange-500"
        />
        
        {/* Icon */}
        <div className={`p-2 rounded-lg ${config.bgColor}`}>
          <IconComponent className={`w-5 h-5 ${config.color}`} />
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-sm font-medium ${config.color}`}>
              {notification.label}
            </span>
            {isUnread && (
              <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30 text-xs">
                New
              </Badge>
            )}
          </div>
          
          <p className="text-white text-sm mb-2">{notification.message}</p>
          
          <div className="flex items-center gap-4 text-xs text-zinc-500">
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {formatTimeAgo(notification.created_at)}
            </span>
            {notification.actor_name && (
              <span>by {notification.actor_name}</span>
            )}
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex items-center gap-1">
          {targetLink && (
            <Button
              size="sm"
              variant="ghost"
              className="h-8 w-8 p-0"
              onClick={() => navigate(targetLink)}
              title="View details"
            >
              <ExternalLink className="w-4 h-4" />
            </Button>
          )}
          {isUnread && (
            <Button
              size="sm"
              variant="ghost"
              className="h-8 w-8 p-0 text-green-400 hover:text-green-300"
              onClick={() => handleMarkRead([notification.id])}
              disabled={actionLoading}
              title="Mark as read"
            >
              <Check className="w-4 h-4" />
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            className="h-8 w-8 p-0 text-red-400 hover:text-red-300"
            onClick={() => handleDelete(notification.id)}
            disabled={actionLoading}
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-black text-white p-6" data-testid="admin-notifications-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Bell className="w-6 h-6 text-orange-400" />
              Admin Notifications
            </h1>
            <p className="text-zinc-400 text-sm mt-1">
              {counts.unread > 0 ? (
                <span className="text-orange-400">{counts.unread} unread</span>
              ) : (
                'All caught up!'
              )}
              {' · '}{counts.total} total
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            {counts.unread > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleMarkAllRead}
                disabled={actionLoading}
                className="border-zinc-700"
              >
                <CheckCheck className="w-4 h-4 mr-1" />
                Mark All Read
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                fetchNotifications(true);
                fetchCount();
              }}
              disabled={loading}
              className="border-zinc-700"
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card className="bg-zinc-900 border-zinc-800 mb-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-zinc-500" />
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-32 bg-zinc-800 border-zinc-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="unread">Unread</SelectItem>
                    <SelectItem value="read">Read</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-44 bg-zinc-800 border-zinc-700">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="all_types">All Types</SelectItem>
                  <SelectItem value="user_signup">User Signups</SelectItem>
                  <SelectItem value="comic_created">New Comics</SelectItem>
                  <SelectItem value="chapter_published">New Chapters</SelectItem>
                  <SelectItem value="artwork_uploaded">New Artwork</SelectItem>
                  <SelectItem value="content_reported">Reports</SelectItem>
                  <SelectItem value="novel_created">New Novels</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Bulk Actions */}
        {selectedIds.size > 0 && (
          <Card className="bg-orange-500/10 border-orange-500/30 mb-4">
            <CardContent className="p-3 flex items-center justify-between">
              <span className="text-orange-400 font-medium">
                {selectedIds.size} selected
              </span>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="border-green-500/30 text-green-400 hover:bg-green-500/10"
                  onClick={() => handleMarkRead()}
                  disabled={actionLoading}
                >
                  <Check className="w-4 h-4 mr-1" />Mark Read
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-zinc-400"
                  onClick={() => {
                    setSelectedIds(new Set());
                    setSelectAll(false);
                  }}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Select All */}
        <div className="flex items-center gap-4 mb-4 px-4">
          <Checkbox
            checked={selectAll}
            onCheckedChange={toggleSelectAll}
            className="data-[state=checked]:bg-orange-500"
          />
          <span className="text-sm text-zinc-500">Select all</span>
        </div>

        {/* Notifications List */}
        <div className="space-y-3">
          {loading && notifications.length === 0 ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
            </div>
          ) : notifications.length === 0 ? (
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="py-16 text-center">
                <BellOff className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-white mb-2">No notifications</h3>
                <p className="text-zinc-500">
                  {statusFilter === 'unread' 
                    ? "You're all caught up! No unread notifications."
                    : 'No notifications match the current filters.'}
                </p>
              </CardContent>
            </Card>
          ) : (
            notifications.map(renderNotification)
          )}
        </div>

        {/* Load More */}
        {hasMore && (
          <div className="flex justify-center mt-6">
            <Button
              variant="outline"
              onClick={() => fetchNotifications(false)}
              disabled={loading}
              className="border-zinc-700"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <ChevronDown className="w-4 h-4 mr-2" />
              )}
              Load More
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminNotifications;
